<template>
    <div>

        <vue-headful title="Incidents :: MeritEHS"/>

        <h4 class="pb-sm-3 pb-2 pt-3 mb-2 mb-sm-2">
            <ol class="breadcrumb font-weight-bold m-0">
                <li class="breadcrumb-item active"><i class="fa fa-stethoscope"></i> Incident Root Cause Trends</li>
                <li class="ml-3 d-sm-inline-block d-none">
                    <router-link :to="`/incidents/create`" class="btn btn-sm btn-success"><i class="fa fa-plus-circle"></i> Create Incident</router-link>
                </li>
            </ol>
        </h4>

        <div class="d-sm-none d-block mb-3">
            <router-link :to="`/incidents/create`" class="btn btn-sm btn-success"><i class="fa fa-plus-circle"></i> Create Incident</router-link>
        </div>

        <div class="card">
            <div class="card-body">
                <v-client-table :name="tableName" :ref="tableName" :columns="columns" :options="options" :data="data">

                    <div slot="afterFilterWrapper" class="ml-md-2 ml-0 mt-md-0 mt-2 mb-md-0 mb-3 float-md-left">
                        <div class="clearfix d-md-none d-block"></div>

                        <div class="float-left">
                            <table-filters-2 ref="incRootCauseTableFilters" filterUrl="/incident/filters" v-bind:filters="filters" :emptyFilters="filterDefs" :filterKey="filterKey" @filtersChanged="refreshTableData" @filtersCleared="filtersCleared">
                                <template slot-scope="slotProps">
                                    <div class="d-inline-block">
                                        <multi-select v-model="slotProps.filters['incident.IncidentTypeId']" :btnLabel="(values) => ('Incident Types ('+values.length+')')" :selectOptions="slotProps.filterData.incidentTypes" :options="{multi: true}"></multi-select>
                                    </div>
                                    <div class="d-inline-block">
                                        <multi-select v-model="slotProps.filters['incident.InjuryTypeId']" :btnLabel="(values) => ('Injury Types ('+values.length+')')" :selectOptions="slotProps.filterData.injuryTypes" :options="{multi: true}"></multi-select>
                                    </div>
                                    <div class="d-inline-block">
                                        <multi-select v-model="slotProps.filters['jobsite.DivisionId']" :btnLabel="(values) => ('Divisions ('+values.length+')')" :selectOptions="slotProps.filterData.divisions" :options="{multi: true}"></multi-select>
                                    </div>
                                    <div class="d-inline-block">
                                        <date-range-selector buttonTitle="Incident Date" v-model="slotProps.filters['IncidentDate']" />
                                    </div>
                                    <div class="d-inline-block" style="width: 300px; margin-top: .5rem;">
                                        <jobsite-select :selectedValues="slotProps.filters['JobsiteId']" :forFilters="true" v-on:vselected="((data) => { slotProps.filters['JobsiteId'] = data || {};  })" />
                                    </div>
                                    <div class="d-inline-block">
                                        <multi-select v-model="slotProps.filters['behavior_id']" :btnLabel="(values) => ('Behaviors ('+values.length+')')" :selectOptions="slotProps.filterData.behaviors" :options="{multi: true}"></multi-select>
                                    </div>
                                    <div class="d-inline-block">
                                        <multi-select v-model="slotProps.filters['condition_id']" :btnLabel="(values) => ('Conditions ('+values.length+')')" :selectOptions="slotProps.filterData.conditions" :options="{multi: true}"></multi-select>
                                    </div>
                                    <div class="d-inline-block">
                                        <multi-select v-model="slotProps.filters['hazard_id']" :btnLabel="(values) => ('Hazards ('+values.length+')')" :selectOptions="slotProps.filterData.hazards" :options="{multi: true}"></multi-select>
                                    </div>
                                </template>
                            </table-filters-2>
                        </div>

                        <button type="button" class="btn btn-outline-success ml-2" v-on:click="exportIncidents()" :disabled="exporting">
                            <i class="fa fa-file-excel-o"></i>
                            <span v-show="!exporting">Export</span>
                            <span v-show="exporting">Exporting <i class="fa fa-spinner fa-spin"></i></span>
                        </button>

                        <div class="clearfix d-md-none d-block"></div>
                    </div>

                    <router-link slot="IncidentId" slot-scope="props" :to="`/incidents/view/${props.row.IncidentId}`" title="View Incident" class="btn btn-sm btn-light">
                        View - {{ props.row.IncidentId }}
                    </router-link>

                    <template slot="employee_name" slot-scope="props">
                        <router-link :to="`/employees/view/${props.row.EmployeeId}`" title="View Employee">
                            {{ props.row.employee_name }}

                        </router-link>
                        <template v-if="props.row.additional_employees && props.row.additional_employees.length > 0">
                            + {{ props.row.additional_employees.length }} More
                        </template>
                    </template>

                    <template slot="IncidentDate" slot-scope="props">
                        {{ props.row.IncidentDate | moment("MM/DD/YYYY") }}
                    </template>

                    <template slot="status_name" slot-scope="props">
                        <label :class="`badge badge-${props.row.status_color}`">{{ props.row.status_name }}</label>
                    </template>

                    <template slot="JobsiteName" slot-scope="props">
                        <router-link :to="`/jobsites/view/${props.row.JobsiteId}`" title="View Jobiste" v-if="props.row.JobsiteId">
                            {{ props.row.JobsiteName }}
                        </router-link>
                        <span title="Non-Jobsite Location" v-else>
                            {{ props.row.other_location }}
                        </span>
                    </template>

                    <template slot="created_at" slot-scope="props">
                        {{ props.row.created_at | moment('timezone', userTimezone, "MM/DD/YYYY hh:mm a") }}
                    </template>

                    <template slot="condition_name" slot-scope="props">
                        <template v-if="props.row.behavior_name">Behavior</template>
                        <template v-else-if="props.row.condition_name">Condition</template>
                        <template v-else-if="props.row.hazard_name">Hazard</template>
                    </template>

                    <template slot="behavior_name" slot-scope="props">
                        <template v-if="props.row.behavior_name">{{ props.row.behavior_name }}</template>
                        <template v-else-if="props.row.condition_name">{{ props.row.condition_name }}</template>
                        <template v-else-if="props.row.hazard_name">{{ props.row.hazard_name }}</template>
                    </template>
                </v-client-table>
            </div>
        </div>
    </div>
</template>

<script>
    import moment from 'moment-timezone';
    import filterExtractor from '@/helpers/TableFilterExtractor';
    import customVTColDropdown from '@/components/vue-tables-2/CustomColumnDropdown';

    export default {
        data: function() {
            return {
                exporting: false,
                tableName: "incidentRootCauseTable",
                filterDefs: {
                    'incident.IncidentTypeId': [],
                    'incident.InjuryTypeId': [],
                    'jobsite.DivisionId': [],
                    'IncidentDate' : 'ytd',
                    'JobsiteId': {},
                    'behavior_id': [],
                    'condition_id': [],
                    'hazard_id': [],
                },
                filterKey: 'inciRootCauseFilters',
                userTimezone: this.$store.state.user.timezone,
                columns: ['IncidentId', 'employee_name', 'IncidentDate', 'status_name', 'IncidentTypeName', 'InjuryTypeName', 'DivisionName', 'JobsiteName', 'JobsiteNumber', 'user_name', 'created_at', 'condition_name', 'behavior_name'],
                data: [],
            }
        },

        watch: {
            appliedFilter(newVal, oldVal){
                //Call the helper
                this.checkForAppliedFilter();
            },
        },

        computed: {
            filters(){
                //Check State
                if(this.$store.state.tableFilters.filters[this.filterKey])
                {
                    //Send it
                    return this.$store.state.tableFilters.filters[this.filterKey];
                }

                //Return Definition
                return JSON.parse(JSON.stringify(this.filterDefs));
            },

            appliedFilter(){
                //Send it
                return this.$store.state.appliedFilters[this.filterKey] ? this.$store.state.appliedFilters[this.filterKey] : null;
            },

            options(){
                return {
                    headings: {
                        IncidentId: 'ID',
                        employee_name: 'Employee',
                        IncidentDate: 'Incident Date',
                        status_name: 'Status',
                        total_cost: 'Total',
                        IncidentTypeName: 'Type',
                        InjuryTypeName: 'Injury',
                        DivisionName: 'Division',
                        JobsiteName: 'Jobsite',
                        JobsiteNumber: 'Jobsite#',
                        user_name: 'Creator',
                        created_at: 'Created',
                        condition_name: 'Root Cause Type',
                        behavior_name: 'Root Cause Name',
                    },
                    saveState: true,
                    columnsDropdown: customVTColDropdown,
                    visibleColumns: this.$root.getVisibleColumnsFromState(this.tableName),
                    orderBy: {
                        ascending: false,
                        column: 'IncidentDate',
                    },
                    filterable: ['status_name', 'IncidentTypeName', 'InjuryTypeName', 'DivisionName', 'JobsiteName', 'JobsiteNumber', 'user_name', 'condition_name', 'behavior_name', 'hazard_name'],
                    customSorting: {
                        behavior_name: function(ascending) {
                            return function(a, b) {
                                let a_compare = '';
                                if (a.behavior_name)
                                {
                                    a_compare = a.behavior_name;
                                }
                                else if (a.condition_name)
                                {
                                    a_compare = a.condition_name;
                                }
                                else if (a.hazard_name)
                                {
                                    a_compare = a.hazard_name;
                                }

                                let b_compare = '';
                                if (b.behavior_name)
                                {
                                    b_compare = b.behavior_name;
                                }
                                else if (b.condition_name)
                                {
                                    b_compare = b.condition_name;
                                }
                                else if (b.hazard_name)
                                {
                                    b_compare = b.hazard_name;
                                }

                                if (ascending)
                                {
                                    return a_compare < b_compare ? 1 : -1;
                                }
                                else
                                {
                                    return a_compare > b_compare ? 1 : -1;
                                }
                            }
                        },
                        condition_name: function(ascending) {
                            return function(a, b) {
                                let a_compare = '';
                                if (a.behavior_name)
                                {
                                    a_compare = 'Behavior';
                                }
                                else if (a.condition_name)
                                {
                                    a_compare = 'Condition';
                                }
                                else if (a.hazard_name)
                                {
                                    a_compare = 'Hazard';
                                }

                                let b_compare = '';
                                if (b.behavior_name)
                                {
                                    b_compare = 'Behavior';
                                }
                                else if (b.condition_name)
                                {
                                    b_compare = 'Condition';
                                }
                                else if (b.hazard_name)
                                {
                                    b_compare = 'Hazard';
                                }

                                if (ascending)
                                {
                                    return a_compare < b_compare ? 1 : -1;
                                }
                                else
                                {
                                    return a_compare > b_compare ? 1 : -1;
                                }
                            }
                        },
                    },
                };
            },
        },

        methods: {
            getData() {
                this.$http.get('incident/root-cause-list', {filters: filterExtractor.extractFilters(this.filters)})
                    .then(res => {
                        //Set data
                        this.data = res.data;
                    })
                    .catch(err => {

                    })
                    .then(() => {
                        //Reset
                        this.loading = false;
                    });
            },

            exportIncidents() {
                //Set the helper
                this.exporting = true;

                //Build the payload
                var payload = {
                    filters: filterExtractor.extractFilters(this.filters),
                    query: (this.$refs.incidentRootCauseTable.getRequestParams()).query,
                    orderBy: this.$refs.incidentRootCauseTable.options.orderBy.column,
                    ascending: this.$refs.incidentRootCauseTable.options.orderBy.ascending,
                    table: 'incident-root-cause-table',
                    columns: this.columns,
                    visible_columns: this.$root.getVisibleColumnsFromState(this.tableName),
                    headings: this.options.headings,
                    export: true,
                };

                //Send it
                this.$http.get('incident/root-cause-list', payload)
                    .then(response => {
                        //Open the new window
                        window.open(response.data);
                    })
                    .catch(err => {})
                    .then(response => {
                        //reset
                        this.exporting = false;
                    });
            },

            refreshTableData(filters) {
                //Set the data
                this.$refs.incidentRootCauseTable.setPage(1);
                this.getData();
            },

            filtersCleared(){
                //Reset
                this.refreshTableData();

                //Reset the Columns
                this.$refs.incRootCauseTableFilters.resetColVis(this.$refs.incidentRootCauseTable);
            },

            // setUserFilterData(){
            //     //Call the Helper
            //     this.$refs.inspSavedFilters.setFilterParams(this.$refs.incidentRootCauseTable);
            // },

            checkForAppliedFilter(){
                //Check if
                if(!this.allowFilterSaving || !this.appliedFilter)
                {
                    return;
                }

                //Call the helper
                this.$refs.incRootCauseTableFilters.setColVis(this.$refs.incidentRootCauseTable);
            },
        },

        mounted(){
            //Check it
            this.checkForAppliedFilter();

            //Get it
            this.getData();

            //Focus on the SEarch Input
            this.$refs.incidentRootCauseTable.$refs.genericFilter.focus();
        },
    }
</script>